const { Observable, BehaviorSubject } = require('rxjs');
const {
  isNotFound,
  split,
  topMatch,
  error,
  isPromise,
  toObservable,
  addPathPrefix,
  removePathPrefix,
  ref,
  isRef,
  parseRef,
  watchValues,
  getValues,
  getValueType,
  isDynamicRoute,
  MusterError,
  PENDING,
  NOT_FOUND,
  INVALIDATE,
} = require('../../muster-utils');
const flow = require('lodash/flow');
const uniq = require('lodash/uniq');
const omit = require('lodash/omit');
const xor = require('lodash/xor');
const difference = require('lodash/difference');
const identity = require('lodash/identity');

/**
 * RouteHandler
 * Route handlers are registered with a Muster instance, and are invoked to process requests. They
 * handlers can specify `get`, `set` and `call` method handlers to process different types of
 * requests. Route handers are generally created internally within Muster, via the
 * `parseRouteHandler()` method, which converts a user-provided route handler definition into a
 * valid route handler instance.
 * @classdesc
 */
class RouteHandler {
  constructor({ get, set, call }) {
    /**
     * 'get' method handler
     * @type {GetMethodHandler}
     */
    this.get = get;
    /**
     * 'set' method handler
     * @type {SetMethodHandler}
     */
    this.set = set;
    /**
     * 'call' method handler
     * @type {SetMethodHandler}
     */
    this.call = call;
  }
}

module.exports = {
  RouteHandler,

  /**
   * Parse a user-provided route handler definition into a fully-fledged route handler
   * @param {RouteHandlerDefinition|RouteHandler} handlerDefinition Route handler definition
   * @param {RoutePattern|Array<RoutePattern>} routePattern Route pattern at which the route handler
   * will be mounted
   * @throws {Error} Throws an error if no handler definition is specified
   * @throws {Error} Throws an error if no route pattern is specified
   * @returns {RouteHandler} Route handler object
   */
  parseRouteHandler(handlerDefinition, routePattern) {
    if (!handlerDefinition) {
      throw new Error('No handler definition specified');
    }
    if (!routePattern) {
      throw new Error('No route pattern specified');
    }
    if (handlerDefinition instanceof RouteHandler) {
      return new RouteHandler(handlerDefinition);
    }
    const handlerMethods = (
      typeof handlerDefinition === 'function' ?
        { get: handlerDefinition } :
        handlerDefinition
    );
    return new RouteHandler({
      get: parseGetHandler(handlerMethods.get, routePattern),
      set: parseSetHandler(handlerMethods.set, routePattern),
      call: parseCallHandler(handlerMethods.call, routePattern),
    });
  },

  /**
   * Create a route handler that wraps the supplied route handler, prefixing all paths that are
   * passed into it when the route handler is executed.
   * This is useful for positioning a route handler at a different point in the graph (e.g. when
   * importing a module's routes into a parent module)
   * @param {RouteHandler} handler Route handler to prefix
   * @param {Path} [prefix] Path to prepend to the route handler paths
   * @throws {Error} Throws an error if no route handler is specified
   * @returns {RouteHandler} Wrapped route handler object
   */
  prefixRouteHandlerPaths(handler, prefix) {
    if (!handler) {
      throw new Error('No handler specified');
    }
    return new RouteHandler({
      get: prefixGetHandlerPaths(handler.get, prefix),
      set: prefixSetHandlerPaths(handler.set, prefix),
      call: prefixCallHandlerPaths(handler.call, prefix),
    });
  },

  /**
   * Create a route handler that acts as an alias from one point in the graph to another.
   * @param {Path} targetPath Target graph leaf path
   * @param {RouteDependencyMethods} methods Route handler methods to alias
   * @throws {Error} Throws an error if no target path is specified
   * @throws {Error} Throws an error if no route handler methods are specified
   * @returns {RouteHandler} Route handler object
   */
  createAliasHandler(targetPath, methods) {
    if (!targetPath) {
      throw new Error('No target path specified');
    }
    if (!methods) {
      throw new Error('No alias methods specified');
    }
    return new RouteHandler({
      get: methods.get ? createAliasedGetHandler(targetPath) : null,
      set: methods.set ? createAliasedSetHandler(targetPath) : null,
      call: methods.call ? createAliasedCallHandler(targetPath) : null,
    });
  },
};

/**
 * Convert a get handler definition function that accepts { req, res } objects and emits a
 * value/promise/observable into a standardised get handler that accepts path strings and emits a
 * reloadable observable
 * @param {Function} handler Get handler definition function
 * @param {RoutePattern|Array<RoutePattern>} routePattern Handler route patttern
 * @return {GetMethodHandler} Standardised get handler function
 */
function parseGetHandler(handler, routePattern) {
  if (!handler) { return null; }
  const isSinglePathHandler = !isDynamicRoute(routePattern);

  // Define a chain of transformations to be applied to the handler's input
  const inputTransforms = [
    // 1. Convert the input path strings to a set of { req, res } objects for use in handlers
    parseGetHandlerRequests(routePattern),
    // 2. If this is a single path handler, extract the first request from the request set
    (isSinglePathHandler ? formatSingleGetHandlerArguments() : identity),
  ];

  // Define a chain of transformations to be applied to the handler's output
  const outputTransforms = [
    // 1. Convert the handler output to an observable stream
    createGetHandlerOutputStream(),
    // 2. If this is a single path handler, transform the single-item stream into an array stream
    (isSinglePathHandler ? formatSingleGetHandlerOutputStream() : identity),
    // 3. Ensure that the handler emissions are formatted correctly
    validateGetHandlerOutputStream(routePattern),
    // 4. Catch any errors and emit them as as { path, value } objects
    catchGetHandlerErrors(routePattern),
    // 5. Append 'notFound' atoms for parent branches that don't exist
    appendMissingParentBranchAtoms(),
    // 6. Add a 'reload' method to the output stream to allow re-fetching of new paths
    createReloadableGetHandlerOutputStream(),
  ];

  // Compose a handler that wraps the handler definition with the input and output transforms
  return flow(
    ...inputTransforms.slice().reverse(), // Input transforms are processed in reverse order
    ...outputTransforms
  )(handler);
}

function formatSingleGetHandlerArguments() {
  return (handler) =>
    ([request], ...args) => handler(request, ...args);
}

function parseGetHandlerRequests(routePattern) {
  const routes = Array.isArray(routePattern) ? routePattern : [routePattern];
  return (handler) =>
    (paths, ...args) => {
      const requests = paths.map((path) => {
        const { params, routePath } = topMatch(path, routes);
        return {
          req: {
            path,
            params: omit(params, '$path'),
            route: routePath,
          },
          res: {
            value(value) { return ({ path, value }); },
            redirect(target) { return ({ path, value: ref(target) }); },
            error(value) { return ({ path, value: error(value) }); },
            pending() { return ({ path, value: PENDING }); },
            notFound(parentPath) {
              if (parentPath) {
                const isValidParentPath = containsPath(parentPath, path);
                if (!isValidParentPath) {
                  throw new Error('Invalid res.notFound() path:' +
                    ` "${parentPath}" is not an ancestor of "${path}"`
                  );
                }
                return ({ path, value: Object.assign({}, NOT_FOUND, { path: parentPath }) });
              }
              return ({ path, value: NOT_FOUND });
            },
          },
        };
      });
      return handler(requests, ...args);
    };
}

function createGetHandlerOutputStream() {
  return (handler) =>
    (paths, ...args) => {
      const response = handler(paths, ...args);
      return toObservable(response);
    };
}

function formatSingleGetHandlerOutputStream() {
  return (handler) =>
    (paths, ...args) =>
      handler(paths, ...args).map((pathValue) => [pathValue]);
}

function validateGetHandlerOutputStream(routePattern) {
  return (handler) =>
    (paths, ...args) =>
      handler(paths, ...args)
        .do((pathValues) => {
          const validationErrors = validateGetHandlerResponseObject(pathValues);
          if (validationErrors) {
            throw new Error(
              `Invalid get handler return value for ${JSON.stringify(routePattern)}` +
              `${validationErrors.map((message) => `\n ${message}`).join('')}`
            );
          }
        });
}

function catchGetHandlerErrors(routePattern) {
  return (handler) =>
    (paths, ...args) => {
      try {
        return handler(paths, ...args)
          .catch((e) => Observable.of(createPathErrors(e, paths, routePattern)));
      } catch (e) {
        return Observable.of(createPathErrors(e, paths, routePattern));
      }
    };

  function createPathErrors(e, paths, routePath) {
    return paths.map((path) => ({
      path,
      value: error(new MusterError(e.message, {
        code: (e instanceof MusterError ? e.code : MusterError.HANDLER_ERROR),
        path,
        route: routePath,
        method: 'get',
        stack: e.stack,
      })),
    }));
  }
}

function validateGetHandlerResponseObject(pathValues) {
  const isValidPathSet = getIsValidPathValueSet(pathValues);
  if (!isValidPathSet) {
    return [
      'Expected:',
      ' Array<{path, value}>',
      ' Promise<Array<{path, value}>>',
      ' Observable<Array<{path, value}>>',
      'Received:',
      ` ${getValueType(pathValues)}`,
    ];
  }
  return undefined;
}

function appendMissingParentBranchAtoms() {
  return (handler) =>
    (paths, ...args) =>
      handler(paths, ...args)
        .map((pathValues) => {
          // Collect the set of paths which specify a missing parent branch
          const missingPathValues = pathValues.filter(
            ({ value }) => isNotFound(value) && value.path
          );
          if (missingPathValues.length === 0) { return pathValues; }

          // Get a deduplicated list of all the missing parent branches
          const missingParentPaths = uniq(missingPathValues.map(({ value }) => value.path));
          const missingParentPathValues = missingParentPaths.map(
            (path) => ({ path, value: NOT_FOUND })
          );

          // Get a list of path values with non-standard atoms replaced by standard 'notFound' atoms
          const normalizedPathValues = pathValues.map((pathValue) => (
            isNotFound(pathValue.value) && pathValue.value.path ?
              { path: pathValue.path, value: NOT_FOUND } :
              pathValue
          ));

          // Prepend the missing parent branch path values to the normalised result set
          return missingParentPathValues.concat(normalizedPathValues);
        });
}

function createReloadableGetHandlerOutputStream() {
  // This takes the output of the get handler stream and adds an additional `reload()` method that
  // triggers the handler to be re-executed (this is used internally when paths are invalidated by
  // `call` operations).
  return (handler) =>
    (paths, ...args) => {
      // Create a stream that emits the most-recently-requested path set for this handler. This acts
      // as an input stream that is fed into the handler. The first emission consists of the
      // requested path set, then any calls to the `reload()` method will trigger an emission
      // comprising the updated path set values.
      const pathSubject = new BehaviorSubject(paths);
      const responseStream = pathSubject.switchMap((updatedPaths) =>
        handler(updatedPaths, ...args)
      );
      // The `reload` method is used internally to re-fetch data invalidated by `call` operations
      responseStream.reload = (updatedPaths) => pathSubject.next(updatedPaths);
      return responseStream;
    };
}

function parseSetHandler(handler, routePattern) {
  if (!handler) { return null; }
  const isSinglePathHandler = !isDynamicRoute(routePattern);
  const inputTransforms = [
    parseSetHandlerRequests(routePattern),
    (isSinglePathHandler ? formatSingleSetHandlerArguments() : identity),
  ];

  const outputTransforms = [
    (isSinglePathHandler ? formatSingleSetHandlerOutput() : identity),
    validateSetHandlerOutput(routePattern),
    catchSetHandlerErrors(routePattern),
  ];

  return flow(
    ...inputTransforms.reverse(),
    ...outputTransforms
  )(handler);
}

function formatSingleSetHandlerArguments() {
  return (handler) =>
    ([request], ...args) => handler(request, ...args);
}

function parseSetHandlerRequests(routePattern) {
  const routes = Array.isArray(routePattern) ? routePattern : [routePattern];
  return (handler) =>
    (pathValues, ...args) => {
      const requests = pathValues.map(({ path, value }) => {
        const { params, routePath } = topMatch(path, routes);
        return {
          req: {
            path,
            params: omit(params, '$path'),
            value,
            route: routePath,
          },
          res: {
            ok() { return ({ path, value }); },
            redirect(target) { return ({ path, value: ref(target) }); },
            error(message) { return ({ path, value: error(message) }); },
          },
        };
      });
      return handler(requests, ...args);
    };
}

function formatSingleSetHandlerOutput() {
  return (handler) =>
    (pathValues, ...args) => {
      const response = handler(pathValues, ...args);
      return maybePromise(response).then((returnValue) => [returnValue]);
    };
}

function catchSetHandlerErrors(routePattern) {
  return (handler) =>
    (pathValues, ...args) => {
      try {
        const response = handler(pathValues, ...args);
        if (!isPromise(response)) { return response; }
        return response.catch((e) => {
          throw createError(e, pathValues, routePattern);
        });
      } catch (e) {
        throw createError(e, pathValues, routePattern);
      }
    };

  function createError(e, pathValues, routePath) {
    return new MusterError(e.message, {
      code: (e instanceof MusterError ? e.code : MusterError.HANDLER_ERROR),
      path: pathValues,
      route: routePath,
      method: 'set',
      stack: e.stack,
    });
  }
}

function validateSetHandlerOutput(routePattern) {
  return (handler) =>
    (pathValues, ...args) => {
      const response = handler(pathValues, ...args);
      return maybePromise(response)
        .then((returnValue) => {
          const validationErrors = validateSetHandlerResponseObject(returnValue, pathValues);
          if (validationErrors) {
            throw new Error(
              `Invalid set handler return value for ${JSON.stringify(routePattern)}` +
              `${validationErrors.map((message) => `\n ${message}`).join('')}`
            );
          }
          return returnValue;
        });
    };
}

function validateSetHandlerResponseObject(setResponse, inputPathValues) {
  const isValidPathSet = getIsValidPathValueSet(setResponse);
  if (!isValidPathSet) {
    return [
      'Expected:',
      ' Array<{path, value}>',
      ' Promise<Array<{path, value}>>',
      'Received:',
      ` ${getValueType(setResponse)}`,
    ];
  }
  const inputPaths = inputPathValues.map(({ path }) => path);
  const outputPaths = setResponse.map(({ path }) => path);
  const pathsAreEqual = (xor(inputPaths, outputPaths).length === 0);
  if (!pathsAreEqual) {
    const missingPaths = difference(inputPaths, outputPaths);
    const unexpectedPaths = difference(outputPaths, inputPaths);
    return []
      .concat(missingPaths.length > 0 ?
        `Missing values for ${missingPaths.map((path) => `"${path}"`).join(', ')}`
        : []
      ).concat(unexpectedPaths.length > 0 ?
        `Unexpected values received for ${unexpectedPaths.map((path) => `"${path}"`).join(', ')}`
        : []
      );
  }
  return undefined;
}

function parseCallHandler(handler, routePattern) {
  if (!handler) { return null; }
  return flow(
    parseCallHandlerRequest(routePattern),
    validateCallHandlerOutput(routePattern),
    catchCallHandlerErrors(routePattern)
  )(handler);
}

function parseCallHandlerRequest(routePattern) {
  const routes = Array.isArray(routePattern) ? routePattern : [routePattern];
  return (handler) =>
    (callPath, callArgs, ...args) => {
      const { params, routePath } = topMatch(callPath, routes);
      const request = {
        req: {
          args: callArgs,
          path: callPath,
          params: omit(params, '$path'),
          route: routePath,
        },
        res: {
          update(path, value) { return ({ path, value }); },
          error(path, message) { return ({ path, value: error(message) }); },
          pending(path) { return ({ path, value: PENDING }); },
          notFound(path) { return ({ path, value: NOT_FOUND }); },
          invalidate(path) { return ({ path, value: INVALIDATE }); },
        },
      };
      return handler(request, ...args);
    };
}

function validateCallHandlerOutput(routePattern) {
  return (handler) =>
    (path, callArgs, ...args) => {
      const response = handler(path, callArgs, ...args);
      return maybePromise(response)
        .then((callResponse) => {
          const validationErrors = validateCallHandlerResponseObject(callResponse);
          if (validationErrors) {
            throw new Error(
              `Invalid call handler return value for ${JSON.stringify(routePattern)}` +
              `${validationErrors.map((message) => `\n ${message}`).join('')}`
            );
          }
          return {
            response: callResponse && callResponse.response || undefined,
            values: callResponse && callResponse.values || [],
          };
        });
    };
}

function validateCallHandlerResponseObject(callResponse) {
  if (callResponse === undefined) {
    return undefined;
  }
  const isValidCallResponseObject = validateCallResponseObject(callResponse);
  if (!isValidCallResponseObject) {
    return [
      'Expected:',
      ' undefined',
      ' {values?: Array<{path, value}>, response?: any}',
      ' Promise<{values?: Array<{path, value}>, response?: any}>',
      'Received:',
      ` ${getValueType(callResponse)}`,
    ];
  }
  return undefined;

  function validateCallResponseObject(value) {
    if (!value) { return false; }
    if (value.constructor !== Object) { return false; }
    const ALLOWED_KEYS = ['values', 'response'];
    const hasInvalidKeys = Object.keys(value).some((key) => !ALLOWED_KEYS.includes(key));
    if (hasInvalidKeys) { return false; }
    const pathValues = value.values;
    if (pathValues) {
      const isValidPathSet = getIsValidPathValueSet(pathValues);
      if (!isValidPathSet) { return false; }
    }
    return true;
  }
}

function catchCallHandlerErrors(routePattern) {
  return (handler) =>
    (path, callArgs, ...args) => {
      try {
        const response = handler(path, callArgs, ...args);
        if (!isPromise(response)) { return response; }
        return response.catch((e) => {
          throw createError(e, path, callArgs, routePattern);
        });
      } catch (e) {
        throw createError(e, path, callArgs, routePattern);
      }
    };

  function createError(e, pathValues, callArgs, routePath) {
    return new MusterError(e.message, {
      code: (e instanceof MusterError ? e.code : MusterError.HANDLER_ERROR),
      path: pathValues,
      args: callArgs,
      route: routePath,
      method: 'call',
      stack: e.stack,
    });
  }
}

function prefixGetHandlerPaths(handler, prefix) {
  if (!handler) {
    return null;
  }
  // Rewrite the input paths and bound `get` helper, and remap the output paths
  return (paths, { get }) => {
    const prefixedPaths = paths.map((path) => removePathPrefix(path, prefix));
    const injectedGet = prefixInjectedGetMethod(get, prefix);
    const responseStream = handler(prefixedPaths, {
      get: injectedGet,
      watchValues: (pathSet) => watchValues(injectedGet(pathSet)),
    });
    const outputStream = responseStream
      .map((pathValues) =>
        pathValues
          .map(({ path, value }) => ({
            path: addPathPrefix(path, prefix),
            value: (isRef(value) ? ref(addPathPrefix(parseRef(value), prefix)) : value),
          }))
          .filter(({ path }) => path !== null)
      )
      .filter((pathValues) => (pathValues.length > 0));
    outputStream.reload = (updatedPaths) => {
      responseStream.reload(updatedPaths.map((path) => removePathPrefix(path, prefix)));
    };
    return outputStream;
  };
}

function prefixSetHandlerPaths(handler, prefix) {
  if (!handler) {
    return null;
  }
  // Rewrite the input paths and bound `get`/`set` helpers, and remap the output paths
  return (pathValues, { get, set }) => {
    const prefixedPathValues = pathValues.map(({ path, value }) =>
      ({ path: removePathPrefix(path, prefix), value })
    );
    const injectedGet = prefixInjectedGetMethod(get, prefix);
    return maybePromise(handler(prefixedPathValues, {
      get: injectedGet,
      getValues: (paths) => getValues(injectedGet(paths)),
      set: prefixInjectedSetMethod(set, prefix),
    }))
      .then((updatedPathValues) =>
        updatedPathValues.map(({ path, value }) => ({
          path: addPathPrefix(path, prefix),
          value: (isRef(value) ? ref(addPathPrefix(parseRef(value), prefix)) : value),
        }))
      );
  };
}

function prefixCallHandlerPaths(handler, prefix) {
  if (!handler) {
    return null;
  }
  // Rewrite the input paths and bound `get`/`set`/`call` helpers, and remap the output paths
  return (path, callArgs, { get, set, call }) => {
    const prefixedPath = removePathPrefix(path, prefix);
    const injectedGet = prefixInjectedGetMethod(get, prefix);
    return maybePromise(handler(prefixedPath, callArgs, {
      get: injectedGet,
      getValues: (paths) => getValues(injectedGet(paths)),
      set: prefixInjectedSetMethod(set, prefix),
      call: prefixInjectedCallMethod(call, prefix),
    }))
      .then((response) => {
        const prefixedValues = response.values.map(({ path: updatedPath, value }) =>
          ({ path: addPathPrefix(updatedPath, prefix), value })
        );
        return Object.assign({}, response, {
          values: prefixedValues,
        });
      });
  };
}

function prefixInjectedGetMethod(get, prefix) {
  return (querySet) => {
    const paths = Array.isArray(querySet) ? querySet : [querySet];
    const prefixedPaths = paths.map((path) => addPathPrefix(path, prefix));
    return get(prefixedPaths)
      .map((pathValues) =>
        pathValues
          .map(({ path, value }) => ({ path: removePathPrefix(path, prefix), value }))
          .filter(({ path }) => path !== null)
      )
      .filter((pathValues) => pathValues.length > 0);
  };
}

function prefixInjectedSetMethod(set, prefix) {
  return function (pathValueSet) { // eslint-disable-line func-names
    /* eslint-disable */
    if ((arguments.length === 2) && (typeof arguments[0] === 'string')) {
      pathValueSet = { path: arguments[0], value: arguments[1] };
    }
    /* eslint-enable */
    const pathValues = Array.isArray(pathValueSet) ? pathValueSet : [pathValueSet];
    const prefixedPathValues = pathValues.map(({ path, value }) =>
      ({ path: addPathPrefix(path, prefix), value })
    );
    return set(prefixedPathValues)
      .then((returnedValues) =>
        returnedValues
          .map(({ path, value }) => ({ path: removePathPrefix(path, prefix), value }))
      );
  };
}

function prefixInjectedCallMethod(call, prefix) {
  return (path, ...args) => {
    const prefixedPath = addPathPrefix(path, prefix);
    return call(prefixedPath, ...args);
  };
}

function createAliasedGetHandler(targetPath) {
  return (paths, { get }) => {
    const [path] = paths;
    const aliasedPaths = paths.map(() => targetPath);
    return get(aliasedPaths).map((pathValues) =>
      pathValues.map(({ value }) => ({ path, value }))
    );
  };
}

function createAliasedSetHandler(targetPath) {
  return (pathValues, { set }) => {
    const [{ path }] = pathValues;
    const aliasedPathValues = pathValues.map(({ value }) => ({ path: targetPath, value }));
    return set(aliasedPathValues).then((outputPathValues) =>
      outputPathValues.map(({ value }) => ({ path, value }))
    );
  };
}

function createAliasedCallHandler(targetPath) {
  return (path, callArgs, { call }) =>
    call(targetPath, ...callArgs)
      .then((response) => ({
        response,
        values: [],
      }));
}

function getIsValidPathValueSet(pathValues) {
  if (!Array.isArray(pathValues)) {
    return false;
  }
  return pathValues.every((pathValue) =>
    Boolean(pathValue) &&
    ('path' in pathValue) && (typeof pathValue.path === 'string') && (pathValue.path !== '') &&
    ('value' in pathValue)
  );
}

function containsPath(parentPath, childPath) {
  const parentSegments = split(parentPath);
  const childSegments = split(childPath);
  return (childSegments.length > parentSegments.length) && parentSegments.every((segment, index) =>
    (segment === childSegments[index])
  );
}

function maybePromise(value) {
  if (isPromise(value)) {
    return value;
  }
  return {
    then: (callback) => callback(value),
  };
}
